import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import { useSendbirdStateContext } from '@sendbird/uikit-react/useSendbirdStateContext';
import { FAIconName, useToastMessage } from '@equip.health/ui';
import { VITE_SENDBIRD_APP_ID } from '../constants/env';
import { useGlobalContext } from '../contexts/GlobalContext';
import { useLazyGetUserChatTokenQuery } from '~/store/services/chat';
import { useDatadog } from '~/lib/hooks/useDatadog';
import {
  SendbirdReadyContext,
  SendbirdChatType,
} from '../contexts/SendbirdReadyContext';

export const CHAT_HANDLERS_EQUIP_CHANNEL_KEY = 'equip-channel';

// Helper component to access SDK from inside SendbirdProvider
const SendbirdContextUpdater = ({
  setSdkInstance,
}: {
  setSdkInstance: (sdk: SendbirdChatType) => void;
}) => {
  const context = useSendbirdStateContext();
  const sdk = context?.stores?.sdkStore?.sdk;

  useEffect(() => {
    if (sdk) {
      setSdkInstance(sdk);
    }
  }, [sdk, setSdkInstance]);

  return null;
};

const SendBirdChatWrapper = ({ children }: PropsWithChildren) => {
  const { currentUser: provider } = useGlobalContext();
  const { logError } = useDatadog();
  const { createToast } = useToastMessage();
  const [userAccessToken, setUserAccessToken] = useState<string>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const hasShownToast = useRef<boolean>(false);
  const [getUserChatToken, { isFetching }] = useLazyGetUserChatTokenQuery();
  const [sdkInstance, setSdkInstance] = useState<SendbirdChatType | null>(null);

  // Reset sendbird bootstrap when provider changes
  useEffect(() => {
    setIsError(false);
    setUserAccessToken(null);
    hasShownToast.current = false;
  }, [provider?.userId]);

  useEffect(() => {
    if (!provider?.userId || userAccessToken || isFetching || isError) {
      return;
    }

    getUserChatToken()
      .unwrap()
      .then((data) => {
        setUserAccessToken(data?.token);
      })
      .catch((error) => {
        console.error('Failed to get chat token', error);
        logError(error, {
          description: 'Failed to get chat token',
          providerId: provider?.userId,
        });
        setIsError(true);
      });
  }, [
    provider?.userId,
    userAccessToken,
    isFetching,
    isError,
    getUserChatToken,
    logError,
  ]);

  // Show toast once when error occurs
  useEffect(() => {
    if (!isError || hasShownToast.current) {
      return;
    }
    hasShownToast.current = true;
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: 'sendbird-error',
      text: 'Chat and notifications are temporarily unavailable.',
      variant: 'warning',
    });
  }, [isError, createToast]);

  // Auto-disconnect when wrapper unmounts (logout, mode change, etc.)
  useEffect(() => {
    return () => {
      if (sdkInstance && typeof sdkInstance.disconnect === 'function') {
        try {
          sdkInstance.disconnect();
        } catch (error) {
          console.error('Failed to disconnect Sendbird:', error);
        }
      }
    };
  }, [sdkInstance]);

  const isReady = !!userAccessToken && !isError;

  const contextValue = useMemo(() => ({ sdk: sdkInstance }), [sdkInstance]);

  return (
    <SendbirdReadyContext.Provider value={contextValue}>
      {isReady ? (
        <SendbirdProvider
          accessToken={userAccessToken}
          appId={VITE_SENDBIRD_APP_ID}
          data-testid="sendbird-provider"
          isReactionEnabled
          userId={provider?.userId}
        >
          <SendbirdContextUpdater setSdkInstance={setSdkInstance} />
          {children}
        </SendbirdProvider>
      ) : (
        children
      )}
    </SendbirdReadyContext.Provider>
  );
};

export { SendBirdChatWrapper };
